<template>
    <div
        v-show="isOpen"
        :ref="refName"
        class="dropdown-menu"
        :class="[positionClass, dropdownClass, (isOpen && 'dropdown-menu_open')]"
        @keydown="onKeydown"
    >
        <div class="dropdown-menu__wrap">
            <ul
                ref="list"
                class="dropdown-menu__list list"
                role="menu"
                tabindex="-1"
                :aria-hidden="!isOpen"
            >
                <template v-for="(entry, index) in normalizedEntries" :key="entry._key">
                    <li
                        v-if="entry.type === 'separator'"
                        class="dropdown-menu__item item item_type_separator"
                        role="separator"
                    ></li>

                    <li
                        v-else
                        class="dropdown-menu__item item"
                        :class="itemClasses(entry, index)"
                        :data-type="entry.dataType || null"
                        :tabindex="isFocusableItem(entry) ? 0 : -1"
                        role="menuitem"
                        :aria-disabled="entry.disabled ? 'true' : 'false'"
                        :aria-selected="isItemActive(entry) ? 'true' : 'false'"
                        @click.stop="handleSelect(entry)"
                        @mouseenter="setHighlightedIndex(index)"
                        @focus="setHighlightedIndex(index)"
                    >
                        <slot
                            name="item"
                            :item="entry"
                            :index="index"
                            :is-active="isItemActive(entry)"
                            :is-highlighted="highlightedIndex === index"
                            :is-disabled="!!entry.disabled"
                        >
                            <span
                              v-if="showCheckbox"
                              class="dropdown-menu__icon icon icon_type_checkbox"
                            >
                                <span class="icon__symbol">
                                  <SvgIcon class="icon" :name="checkboxIconName" />
                                </span>
                            </span>

                            <span
                                v-if="entry.icon"
                                class="dropdown-menu__icon icon"
                                :class="entry.iconClass"
                            >
                                <span class="icon__symbol">
                                    <SvgIcon
                                        class="icon dropdown-field-icon"
                                        :name="entry.icon"
                                        :style="entry.iconStyle"
                                    />
                                </span>
                            </span>
                            <span class="dropdown-menu__item-name">
                                {{ entry.label }}
                            </span>
                            <span
                                v-if="entry.meta"
                                class="dropdown-menu__item-meta"
                            >
                                {{ entry.meta }}
                            </span>
                        </slot>
                    </li>
                </template>
            </ul>
        </div>
    </div>
</template>

<script>
export default {
    name: 'BaseDropdownList',

    props: {
        refName: {
            type: String,
            default: () => 'BaseDropdownList'
        },
        isOpen: {
            type: Boolean,
            default: false,
        },

        items: {
            type: Array,
            default: () => [],
        },

        sections: {
            type: Array,
            default: () => [
                //minimal item
                // {
                //     label: 'Edit',
                //     value: 'edit'
                // }
                //extended item
                // {
                //     label: 'Delete',
                //     value: 'delete',
                //     icon: 'common__trash',
                //     iconClass: 'is_danger',
                //     iconStyle: { color: 'red' },
                //     disabled: false,
                //     meta: 'Danger action',
                //     isActive: false,
                //     dataType: 'actions',
                //     key: 'delete-action'
                // }
                //section item
                // {
                //     key: 'actions',
                //     items: [
                //         { label: 'Edit', value: 'edit' },
                //         { label: 'Delete', value: 'delete' }
                //     ]
                // }
            ],
        },

        activeValue: {
            type: [String, Number, Boolean, Object, null],
            default: null,
        },

        activeValues: {
            type: Array,
            default: () => [],
        },

        multiple: {
            type: Boolean,
            default: false,
        },

        valueKey: {
            type: String,
            default: 'value',
        },

        labelKey: {
            type: String,
            default: 'label',
        },

        disabledKey: {
            type: String,
            default: 'disabled',
        },

        showCheckbox: {
            type: Boolean,
            default: false,
        },

        checkboxIconName: {
            type: String,
            default: 'common__checkbox',
        },

        positionClass: {
            type: String,
            default: '',
        },

        dropdownClass: {
            type: String,
            default: '',
        },

        itemClass: {
            type: String,
            default: '',
        },

        activeClass: {
            type: String,
            default: 'item_active',
        },

        highlightedClass: {
            type: String,
            default: 'item_highlighted',
        },

        disabledClass: {
            type: String,
            default: 'item_disabled',
        },

        closeOnSelect: {
            type: Boolean,
            default: true,
        },

        closeOnClickOutside: {
            type: Boolean,
            default: true,
        },

        enableKeyboardNavigation: {
            type: Boolean,
            default: true,
        },
    },

    emits: ['select', 'close'],

    data() {
        return {
            highlightedIndex: -1,
        }
    },

    computed: {
        normalizedEntries() {
            if (this.sections?.length) {
                return this.flattenSections(this.sections)
            }

            return this.normalizeItems(this.items)
        },

        focusableIndexes() {
            return this.normalizedEntries
                .map((entry, index) => ({ entry, index }))
                .filter(({ entry }) => this.isFocusableItem(entry))
                .map(({ index }) => index)
        },
    },

    watch: {
        // isOpen(value) {
        //     if (value) {
        //         this.bindClickOutside()
        //         this.$nextTick(() => {
        //             this.initHighlightedIndex()
        //             this.focusHighlightedItem()
        //         })
        //     } else {
        //         this.unbindClickOutside()
        //         this.highlightedIndex = -1
        //     }
        // },
    },
    methods: {
        flattenSections(sections) {
            const result = []

            sections.forEach((section, sectionIndex) => {
                if (sectionIndex > 0) {
                    result.push({
                        type: 'separator',
                        _key: `separator-${sectionIndex}`,
                    })
                }

                const normalizedItems = this.normalizeItems(section.items, section.key)
                result.push(...normalizedItems)
            })

            return result
        },

        normalizeItems(items, sectionKey = null) {
            return (items ?? []).map((item, index) => ({
                ...item,
                type: item.type || 'item',
                label: item[this.labelKey] ?? item.label ?? '',
                value: item[this.valueKey] ?? item.value ?? item.id ?? index,
                disabled: item[this.disabledKey] ?? item.disabled ?? false,
                dataType: item.dataType ?? sectionKey ?? null,
                _key: item.key ?? `${sectionKey || 'item'}-${item[this.valueKey] ?? item.value ?? item.id ?? index}`,
            }))
        },

        getItemValue(item) {
            return item.value
        },

        isItemActive(item) {
            if (item.disabled) return false

            if (typeof item.isActive === 'boolean') {
                return item.isActive
            }

            const itemValue = this.getItemValue(item)

            if (this.multiple) {
                return this.activeValues.includes(itemValue)
            }

            return this.activeValue === itemValue
        },

        isFocusableItem(item) {
            return item.type !== 'separator' && !item.disabled
        },

        itemClasses(item, index) {
            return [
                this.itemClass,
                {
                    [this.activeClass]: this.isItemActive(item),
                    [this.highlightedClass]: this.highlightedIndex === index,
                    [this.disabledClass]: !!item.disabled,
                    'item_has-checkbox': this.showCheckbox,
                },
            ]
        },

        handleSelect(item) {
            if (!this.isFocusableItem(item)) return

            this.$emit('select', item)

            if (this.closeOnSelect) {
                this.$emit('close')
            }
        },

        setHighlightedIndex(index) {
            const entry = this.normalizedEntries[index]
            if (!entry || !this.isFocusableItem(entry)) return
            this.highlightedIndex = index
        },

        initHighlightedIndex() {
            const activeIndex = this.normalizedEntries.findIndex(entry => this.isItemActive(entry))

            if (activeIndex !== -1 && this.isFocusableItem(this.normalizedEntries[activeIndex])) {
                this.highlightedIndex = activeIndex
                return
            }

            this.highlightedIndex = this.focusableIndexes[0] ?? -1
        },

        focusHighlightedItem() {
            if (this.highlightedIndex === -1) return

            const listEl = this.$refs.list
            if (!listEl) return

            const items = listEl.querySelectorAll('[role="menuitem"]')
            const el = items[this.getMenuItemPosition(this.highlightedIndex)]

            if (el) {
                el.focus()
            }
        },

        getMenuItemPosition(entryIndex) {
            const enabledEntriesBefore = this.normalizedEntries
                .slice(0, entryIndex + 1)
                .filter(entry => entry.type !== 'separator').length

            return enabledEntriesBefore - 1
        },

        moveHighlight(direction) {
            if (!this.focusableIndexes.length) return

            const currentPos = this.focusableIndexes.indexOf(this.highlightedIndex)
            const safeCurrentPos = currentPos === -1 ? 0 : currentPos

            let nextPos = safeCurrentPos + direction

            if (nextPos < 0) nextPos = this.focusableIndexes.length - 1
            if (nextPos >= this.focusableIndexes.length) nextPos = 0

            this.highlightedIndex = this.focusableIndexes[nextPos]
            this.$nextTick(this.focusHighlightedItem)
        },

        selectHighlighted() {
            if (this.highlightedIndex === -1) return

            const item = this.normalizedEntries[this.highlightedIndex]
            if (!item) return

            this.handleSelect(item)
        },

        onKeydown(event) {
            if (!this.isOpen || !this.enableKeyboardNavigation) return

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault()
                    this.moveHighlight(1)
                    break
                case 'ArrowUp':
                    event.preventDefault()
                    this.moveHighlight(-1)
                    break
                case 'Enter':
                case ' ':
                    event.preventDefault()
                    this.selectHighlighted()
                    break
                case 'Escape':
                    event.preventDefault()
                    this.$emit('close')
                    break
                case 'Home':
                    event.preventDefault()
                    if (this.focusableIndexes.length) {
                        this.highlightedIndex = this.focusableIndexes[0]
                        this.$nextTick(this.focusHighlightedItem)
                    }
                    break
                case 'End':
                    event.preventDefault()
                    if (this.focusableIndexes.length) {
                        this.highlightedIndex = this.focusableIndexes[this.focusableIndexes.length - 1]
                        this.$nextTick(this.focusHighlightedItem)
                    }
                    break
            }
        },

        bindClickOutside() {
            if (!this.closeOnClickOutside) return
            document.addEventListener('mousedown', this.handleClickOutside)
            document.addEventListener('touchstart', this.handleClickOutside)
        },

        unbindClickOutside() {
            document.removeEventListener('mousedown', this.handleClickOutside)
            document.removeEventListener('touchstart', this.handleClickOutside)
        },

        handleClickOutside(event) {
            const root = this.$refs[this.refName]
            if (!root) return

            if (!root.contains(event.target)) {
                this.$emit('close')
            }
        },
    },
    mounted() {
        // if (this.isOpen) {
        //     this.bindClickOutside()
        // }
    },
    // beforeUnmount() {
    //     this.unbindClickOutside()
    // },


}
</script>

<style scoped>
.item.item_disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
}

.item.item_highlighted {
    background: rgba(0, 0, 0, 0.06);
    outline: none;
}

.dropdown-menu__item-meta {
    margin-left: 8px;
    font-size: 12px;
    opacity: 0.65;
}
</style>